import * as React from 'react'
import { ReactElement, useState } from 'react'
import { AccordionHeader, AccordionPanel, Button, Text } from '@fluentui/react-components'
import { FieldArray, useFormikContext } from 'formik'
import { FamilyLawModuleDto, RequestDto } from '../../../../hooks/module/useFamilyLaw'
import { Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components/unstable'
import { AddCircleRegular, DeleteRegular } from '@fluentui/react-icons'
import RequestDialog from './Dialog/RequestDialog'
import { formatFullNumericDate } from '../../../../helpers/dateHelper'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { FamilyLawAccordionItemProps, FamilyLawDialogAction } from '../../../../pages/File/Registration/FamilyLawOverview'

export type RequestOption = {
    key: ProvisionalType
    text: string
}

export enum RequestType {
    Provisional = 'PROVISIONAL',
    Merit = 'MERIT',
    Appeal = 'APPEAL',
    Cassation = 'CASSATION',
}

export const RequestTypeMap: Map<RequestType, string> = new Map<RequestType, string>([
    [RequestType.Provisional, 'Voorlopig verzoek'],
    [RequestType.Merit, 'Bodemverzoek'],
    [RequestType.Appeal, 'Hoger beroep'],
    [RequestType.Cassation, 'Cassatie'],
])

export enum ProvisionalType {
    UsageHome = 'USAGE_HOME',
    AvailabilityGoods = 'AVAILABILITY_GOODS',
    EntrustChildren = 'ENTRUST_CHILDREN',
    ProvisionalCare = 'PROVISIONAL_CARE',
    Alimony = 'ALIMONY',
    Supervision = 'SUPERVISION',
}

export const ProvisionalRequestOptions: RequestOption[] = [
    { key: ProvisionalType.UsageHome, text: 'Gebruik echtelijke woning' },
    {
        key: ProvisionalType.AvailabilityGoods,
        text: 'Beschikbaar stellen goederen voor dagelijks gebruik (ook voor kinderen)',
    },
    {
        key: ProvisionalType.EntrustChildren,
        text: 'Toevertrouwen minderjarige kinderen, ‘afgifte’ van kinderen en kinderalimentatie',
    },
    {
        key: ProvisionalType.ProvisionalCare,
        text: 'Verdeling zorg- en opvoedingstaken en omgangsregeling en verschaffen informatie over minderjarige kinderen',
    },
    { key: ProvisionalType.Alimony, text: 'Partneralimentatie' },
    { key: ProvisionalType.Supervision, text: 'Onder toezicht stelling' },
]

export const ProvisionalTypeMap: Map<ProvisionalType, string> = new Map(ProvisionalRequestOptions.map(option => [option.key, option.text]))

type RequestItemProps = FamilyLawAccordionItemProps &
    FieldArrayRenderProps & {
        index: number
        request: RequestDto
        onClick: () => void
    }

function RequestItem(props: RequestItemProps): ReactElement {
    return (
        <TreeItem itemType={'leaf'}>
            <TreeItemLayout
                onClick={props.onClick}
                actions={
                    <Button
                        size="small"
                        appearance="transparent"
                        icon={<DeleteRegular />}
                        onClick={(event: React.MouseEvent) => {
                            event.stopPropagation()
                            props.remove(props.index)
                        }}
                    />
                }
            >
                <Text italic={!props.request.decreeDate}>
                    {props.request.referenceDate ? `${formatFullNumericDate(props.request.referenceDate)} | ` : 'Geen datum | '}
                    {RequestTypeMap.get(props.request.type as RequestType)}
                </Text>
            </TreeItemLayout>
        </TreeItem>
    )
}

export default function Requests(props: FamilyLawAccordionItemProps): ReactElement {
    const { values, setErrors } = useFormikContext<FamilyLawModuleDto>()
    const [mutateRequest, setMutateRequest] = useState(false)
    const [action, setAction] = useState<FamilyLawDialogAction>(undefined)
    const [index, setIndex] = useState<number>()

    return (
        <div>
            <AccordionHeader>Procedures</AccordionHeader>
            <AccordionPanel>
                <Tree aria-label={'tree'}>
                    <div>
                        {values?.requests?.length > 0 ? (
                            <FieldArray name="requests">
                                {arrayHelpers => {
                                    return values.requests.map((request, index) => (
                                        <RequestItem
                                            {...props}
                                            key={`request_${index}`}
                                            index={index}
                                            request={request}
                                            {...arrayHelpers}
                                            onClick={() => {
                                                setErrors({})
                                                setAction(FamilyLawDialogAction.Edit)
                                                setIndex(index)
                                                setMutateRequest(true)
                                            }}
                                        />
                                    ))
                                }}
                            </FieldArray>
                        ) : (
                            <TreeItem itemType={'leaf'}>
                                <Text italic size={200}>
                                    Er zijn geen procedures toegevoegd.
                                </Text>
                            </TreeItem>
                        )}
                    </div>
                    <Button
                        size="small"
                        appearance="transparent"
                        icon={<AddCircleRegular />}
                        onClick={() => {
                            setErrors({})
                            setAction(FamilyLawDialogAction.Add)
                            setMutateRequest(true)
                        }}
                    >
                        Procedure toevoegen
                    </Button>
                </Tree>
                {mutateRequest && (
                    <FieldArray name={'requests'}>
                        {arrayHelpers => (
                            <RequestDialog
                                {...props}
                                open={mutateRequest}
                                setOpen={setMutateRequest}
                                action={action}
                                index={index}
                                {...arrayHelpers}
                            />
                        )}
                    </FieldArray>
                )}
            </AccordionPanel>
        </div>
    )
}
